import * as React from 'react'
import { useState } from 'react'
import Button from 'components/Common/Button'
import { DISABLED_BUTTON, PRIMARY_BUTTON, SECONDARY_BUTTON } from 'themes/theme'
import { Box, Typography, Grid } from '@mui/material'
import { IconButton } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import Container from '@mui/material/Container'
import './index.css'
import DetailSection from './businessDetail'
import LocationSection from './businessLocation'
import IndividualSection from './individual'
import { useSelector } from 'react-redux'
import { claimBusiness } from 'store/actions/businessListingActions'
import routes from 'routes'
import { Dialog } from '@progress/kendo-react-dialogs'
import { Close } from '@mui/icons-material'
import MainLayout from 'layouts/mainLayout'
import ZendeskLink from 'components/Common/ZendeskLink'
import iconSuccess from 'assets/icons/fi_check-circle.svg'

const ClaimBusinessDialog = ({ onClose }) => {
  const userInfo = useSelector(state => state.userReducer.user)
  const businessReducer = useSelector(state => state.businessReducer)
  const { selectedBusiness } = businessReducer
  const business = selectedBusiness ? selectedBusiness : null
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const navigate = useNavigate()

  const onKeyPress = React.useCallback(
    event => {
      if (event.code === 'Escape') {
        onClose ? onClose() : navigate(-1)
      }
    },
    [onClose, navigate],
  )

  React.useEffect(() => {
    window.addEventListener('keydown', onKeyPress)
    return () => {
      window.removeEventListener('keydown', onKeyPress)
    }
  }, [onKeyPress])

  const defaultValue = {
    businessName: business ? business.details.businessName || business.company : '',
    legalBusinessName: business ? business.legal_name : '',
    businessPhone: business ? business.details.businessPhone : '',
    yearEstablished: business ? business.details.establishedYear || business.established_year : '',
    industry: business ? business.industry : 'None',
    website: business ? business.details.website || business.web : '',
    description: business ? business.details.businessDescription : '',
    address: {
      street: business ? business.details.addressStreet || business.address : '',
      city: business ? business.details.addressCity || business.city : '',
      state: business ? business.details.addressState || business.state : '',
      zipCode: business ? business.details.addressZip || business.zip : '',
      countryCode: business ? business.details.addressCountry || business.country : '',
      lat: business ? business.details.addressLat || business.lat || 0 : 0,
      lon: business ? business.details.addressLon || business.lon || 0 : 0,
    },
    individual: {
      name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      phone: userInfo?.phone,
      email: userInfo?.email,
    },
    createBusiness: business ? false : true,
    businessId: business ? business.ext_id : '',
  }
  const [claimedData, updateClaimedData] = React.useState(defaultValue)
  const [errors, setErrors] = React.useState({})
  const [status, setStatus] = React.useState({
    loader: false,
    submitted: false,
    alert: false,
  })
  const handleChange = changes => {
    setErrors({})
    updateClaimedData({ ...claimedData, ...changes })
  }

  const isFormValid = () => {
    setErrors({})
    const newErrors = {}
    let isValid = true;
    // TODO: all all field for validation
    // same should be added to every form fields error and helper text
    if (!claimedData.businessPhone) {
      newErrors.businessPhone = 'Required';
      isValid = false
    }
    if (((claimedData.businessPhone).split('_').join('')).length < 10) {
      newErrors.businessPhone = 'Please enter valid phone number';
      isValid = false
    }
    if (!claimedData?.individual?.phone) {
      newErrors.individual = {}
      newErrors.individual.phone = 'Required'
      isValid = false
    }
    if (((claimedData?.individual?.phone).split('_').join('')).length < 10) {
      newErrors.individual = {}
      newErrors.individual.phone = 'Please enter valid phone number'
      isValid = false
    }
    if (claimedData?.yearEstablished) {
      const isValidYear = /^\d{4}$/.test((claimedData.yearEstablished).toString())
      const currentYear = new Date().getFullYear()
      if (!isValidYear || (claimedData.yearEstablished > currentYear)) {
        newErrors.yearEstablished = 'Please enter valid year'
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!isFormValid()) return
    claimBusiness({ ...claimedData, industry: claimedData.industry ? claimedData.industry : 'None' }, stats => {
      setStatus(stats)
    })
    setShowSuccessMessage(true)
  }
  const goToDashboard = () => {
    navigate(routes.home)
    setShowSuccessMessage(false)
  }
  const onDialogClose = () => {
    setShowSuccessMessage(false)
  }
  return (
    <MainLayout>
      {/* <IdleTimer maxIdleTime={process.env['REACT_APP_MAX_IDLE_TIME_DASHBOARD']} /> */}
      <Box className="bg-white claimForm">
        <Box className="d-flex justify-content-end align-items-end p-4">
          <Button onClick={() => (onClose ? onClose() : navigate(-1))}>Exit Claim Business</Button>
        </Box>
        <Container component="form" onSubmit={handleSubmit}>
          <Box className="text-center" color="#25245f">
            <Typography className="mb-3" variant="h5">
              Business Summary
            </Typography>
            <Typography className="text-normal mb-2" variant="body2">
              We understand businesses are growing and things change. For best results please add or update as much
              information as you can that is most representative of your business in the following fields below so we
              can verify your association.
            </Typography>
            <Typography className="text-normal" variant="body2">
              If you don’t have a work email or phone number, please input your personal email and phone number used for
              this business.
            </Typography>
          </Box>
          <DetailSection onchange={handleChange} data={claimedData} errors={errors} />
          <Box style={{ marginTop: '40px' }} className="customDivider"></Box>
          <LocationSection onchange={handleChange} data={claimedData.address} />
          <Box style={{ marginTop: '40px' }} className="customDivider"></Box>
          <IndividualSection onchange={handleChange} data={claimedData.individual} errors={errors} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box className="my-3" display="flex" justifyContent="start" gap="15px">
                <Button
                  style={{ marginTop: '20px' }}
                  submit
                  type={!status.submitted ? PRIMARY_BUTTON : DISABLED_BUTTON}
                  loading={status.loader}
                  disabled={status.submitted}
                >
                  <Box display="flex" alignItems="center" gap="10px">
                    Submit for Verification
                  </Box>
                </Button>
                <Button
                  style={{ marginTop: '20px' }}
                  type={!status.submitted ? SECONDARY_BUTTON : DISABLED_BUTTON}
                  negative
                  disabled={status.submitted}
                  onClick={() => updateClaimedData(defaultValue)}
                  className="px-5 py-3"
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
        {showSuccessMessage && status.submitted && (
          <Dialog width="440px" height="300px">
            <IconButton sx={{ left: "360px" }} onClick={onDialogClose}>
              <Close />
            </IconButton>
            <Box textAlign="center" className="popUp">
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                marginLeft="160px"
                alignItems="flex-start"
              >
                <img src={iconSuccess} style={{ marginTop: '5%', marginBottom: '8%' }} alt="success" height={42} />

              </Box>
              <Box fontSize="20px" marginBottom="6%" fontWeight="bold">
                Success!
              </Box>
              <Box fontSize="14px" marginBottom="10%">
                You'll receive email confirmation of your verification within 24 hrs.
              </Box>
              <Button onClick={goToDashboard}>Go to Dashboard</Button>
            </Box>
          </Dialog>
        )}
        <ZendeskLink />
      </Box>
    </MainLayout>
  )
}

export default ClaimBusinessDialog
